/**
 * DJI México - dataLayer 统一数据契约核心库
 * ------------------------------------------------------------------
 * 职责：只负责「把业务事实写进 dataLayer」。
 * 本文件不包含任何 GA4 / Meta / TikTok 平台逻辑，平台映射全部由 GTM 完成。
 *
 * 对外 API： window.DJIDataLayer
 *   .push(eventName, payload)        推送一个事件（自动补齐公共字段）
 *   .setPageVars()                   推送页面级变量（无 event 键，供 GTM 变量读取）
 *   .uuid()                          生成 event_id
 *   .once(key)                       同一 page_view 内去重（返回 true 表示首次）
 *   .onceSession(key)                同一会话内去重
 *   .observe(el, opts, cb)           曝光观察：可见面积>=50% 且持续>=1s
 *   .onCartSuccess(cb)               购物车接口成功后回调（add / change / update）
 *   .fetchCart()                     读取 /cart.js
 *   .item(productId, extra)          按 items 字段规范构造单个商品对象
 *   .itemsFromCartLines(lines,extra) 由购物车行构造 items[]
 *   .sanitize(text)                  搜索词等自由文本的敏感信息拦截/脱敏
 *   .isInteractive(el)               判断元素是否为「可用元素」（禁用按钮不计）
 *   .clickWillSucceed(evt, el)       判断点击是否产生真实跳转/状态变更
 *
 * 调试： URL 加 ?dji_track_debug=1 或 localStorage.setItem('dji_track_debug','1')
 */
(function (w, d) {
  'use strict';

  if (w.DJIDataLayer) return;

  w.dataLayer = w.dataLayer || [];

  var CTX = w.__djiTrackingContext || {};
  var DEBUG = false;
  try {
    DEBUG = /[?&]dji_track_debug=1/.test(w.location.search) || w.localStorage.getItem('dji_track_debug') === '1';
  } catch (e) {}

  /* ---------------------------------------------------------------- */
  /* 基础工具                                                          */
  /* ---------------------------------------------------------------- */

  function uuid() {
    try {
      if (w.crypto && typeof w.crypto.randomUUID === 'function') return w.crypto.randomUUID();
    } catch (e) {}
    var t = Date.now();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (t + Math.random() * 16) % 16 | 0;
      t = Math.floor(t / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  var PAGE_VIEW_ID = uuid();
  var seenPageView = {};

  /**
   * 客户端路由切换（本站 PDP 的 Instant Switch 秒切）后重开一次 page_view：
   * 重置 page_view_id 与曝光去重表，让新页面的曝光/浏览事件能正常发送。
   * 必须在新 UI 实际渲染完成后调用，不能仅凭 URL 变化调用。
   */
  var pageViewResets = [];

  /** 注册一个「新 page_view 时要重置的闭包状态」，如 scrollDepth 的 maxPct */
  function onNewPageView(fn) {
    if (typeof fn === 'function') pageViewResets.push(fn);
  }

  function newPageView() {
    PAGE_VIEW_ID = uuid();
    seenPageView = {};
    w.DJIDataLayer.pageViewId = PAGE_VIEW_ID;
    /* 仅清去重表不够：scrollDepth 的 maxPct 之类的闭包状态若不重置，
       秒切后新页面的滚动深度会被上一个页面的进度压住。 */
    for (var i = 0; i < pageViewResets.length; i++) {
      try { pageViewResets[i](); } catch (e) { log('pageViewReset error', e); }
    }
    log('new page_view', PAGE_VIEW_ID);
    return PAGE_VIEW_ID;
  }

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['%c[DJI dataLayer]', 'color:#526408;font-weight:bold'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  /* 去重：同一 page_view 内（newPageView 时整表清空） */
  function once(key) {
    if (!key) return true;
    if (seenPageView[key]) return false;
    seenPageView[key] = 1;
    return true;
  }

  /* 去重：同一会话内 */
  function onceSession(key) {
    if (!key) return true;
    /* 注意：去重对调试模式同样生效。调试开关只负责打日志，不改变任何行为，
       否则 QA 在调试模式下看到的与真实用户不一致，也无法验证去重本身。
       测试时如需重新触发，手动清 sessionStorage 里的 dji_tr_* 键或换无痕窗口。 */
    var k = 'dji_tr_' + key;
    try {
      if (w.sessionStorage.getItem(k)) return false;
      w.sessionStorage.setItem(k, '1');
      return true;
    } catch (e) {
      return once(key);
    }
  }

  /* ---------------------------------------------------------------- */
  /* 同意状态                                                          */
  /* ---------------------------------------------------------------- */

  function consent(kind) {
    try {
      var cp = w.Shopify && w.Shopify.customerPrivacy;
      if (cp) {
        if (kind === 'analytics' && typeof cp.analyticsProcessingAllowed === 'function') {
          return !!cp.analyticsProcessingAllowed();
        }
        if (kind === 'marketing' && typeof cp.marketingAllowed === 'function') {
          return !!cp.marketingAllowed();
        }
      }
    } catch (e) {}
    return null; /* 未知：GTM 侧按未同意处理 */
  }

  /* ---------------------------------------------------------------- */
  /* PII 拦截 / 脱敏                                                   */
  /* ---------------------------------------------------------------- */

  var RE_EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  /* 连续 7 位以上数字（含空格 / - / ( ) 分隔）视为电话或订单号 */
  var RE_PHONE = /(?:\+?\d[\s\-().]?){7,}/g;
  var RE_LONGNUM = /\d{7,}/g;

  function sanitize(text) {
    if (text == null) return '';
    var s = String(text).trim().slice(0, 100);
    if (!s) return '';
    var redacted = false;
    if (RE_EMAIL.test(s)) { RE_EMAIL.lastIndex = 0; redacted = true; }
    RE_EMAIL.lastIndex = 0;
    s = s.replace(RE_EMAIL, '[redacted_email]');
    if (RE_PHONE.test(s)) { RE_PHONE.lastIndex = 0; redacted = true; }
    RE_PHONE.lastIndex = 0;
    s = s.replace(RE_PHONE, '[redacted_phone]');
    RE_LONGNUM.lastIndex = 0;
    s = s.replace(RE_LONGNUM, '[redacted_number]');
    if (redacted) log('search term redacted');
    return s.toLowerCase();
  }

  /* ---------------------------------------------------------------- */
  /* 公共字段                                                          */
  /* ---------------------------------------------------------------- */

  /** 设备类型：滚动深度等事件要求携带 */
  function deviceType() {
    var wid = w.innerWidth || d.documentElement.clientWidth || 0;
    if (wid && wid < 768) return 'mobile';
    if (wid && wid < 1024) return 'tablet';
    return 'desktop';
  }

  function commonFields() {
    return {
      event_id: uuid(),
      page_view_id: PAGE_VIEW_ID,
      page_type: CTX.page_type || 'other',
      template_id: CTX.template_id || '',
      page_location: w.location.href,
      page_title: d.title,
      locale: CTX.locale || '',
      currency: CTX.currency || '',
      consent_analytics: consent('analytics'),
      consent_marketing: consent('marketing')
    };
  }

  function clean(obj) {
    var out = {};
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      /* 只丢 undefined / null。空字符串是有意义的取值——
         active_filters:''、current_sort:'' 表示「无筛选/默认排序」，
         丢掉之后 GA4 里「无筛选」与「字段没上报」无法区分，而这些是表里的必传字段。 */
      if (v === undefined || v === null) continue;
      out[k] = v;
    }
    return out;
  }

  /**
   * GA4 电商字段：这些键会被收进 ecommerce 对象，其余业务/来源字段留在事件顶层。
   * 这样 GTM 的 GA4 标签勾上「发送电子商务数据 -> 数据层」即可自动读取，
   * 无需为每个电商事件手动映射 items。
   */
  var ECOM_KEYS = [
    'value', 'items', 'item_list_id', 'item_list_name',
    'coupon', 'transaction_id', 'affiliation', 'shipping', 'tax'
  ];

  /**
   * 推送事件。
   * @param {string} eventName 事件名（不得使用西语展示文本）
   * @param {object} payload   来源字段 / items 等
   *
   * 产出结构：
   *   {
   *     event, event_id, page_type, ... ,        // 公共字段
   *     component, component_id, placement, ... , // 业务来源字段
   *     ecommerce: { currency, value, items[] }   // 仅电商事件才有
   *   }
   */
  function push(eventName, payload) {
    if (!eventName) return null;
    var data = clean(payload || {});

    /* 只要事件「声明了 items」（哪怕是空数组，如空购物车的 view_cart），
       就按电商事件处理：先清场，再把电商字段收进 ecommerce。
       否则 value / item_list_id 会残留在顶层，且上一个电商事件的 items
       会一直挂在 GTM 数据模型里串到这个事件上。 */
    var declaresEcom = Object.prototype.toString.call(data.items) === '[object Array]';
    var ecom = null;
    if (declaresEcom) {
      w.dataLayer.push({ ecommerce: null });
      ecom = {};
      for (var e = 0; e < ECOM_KEYS.length; e++) {
        var ek = ECOM_KEYS[e];
        if (data[ek] !== undefined) {
          ecom[ek] = data[ek];
          delete data[ek];
        }
      }
    } else if (data.items !== undefined) {
      /* items 不是数组：无效取值，丢弃 */
      delete data.items;
    }

    var evt = {};
    var common = commonFields();
    for (var c in common) evt[c] = common[c];
    for (var k in data) evt[k] = data[k];
    if (ecom) {
      /* currency 同时保留在顶层（文档要求的公共字段）与 ecommerce 内（GA4 电商口径） */
      ecom.currency = ecom.currency || common.currency;
      evt.ecommerce = ecom;
    }
    evt.event = eventName;
    w.dataLayer.push(evt);
    log(eventName, evt);
    return evt;
  }

  /** 页面级变量：无 event 键，仅供 GTM 变量读取 */
  function setPageVars(extra) {
    var vars = commonFields();
    delete vars.event_id;
    for (var k in (extra || {})) vars[k] = extra[k];
    w.dataLayer.push(clean(vars));
    log('page vars', vars);
  }

  /* ---------------------------------------------------------------- */
  /* 曝光观察：可见面积 >= 50% 且持续 >= 1s                              */
  /* ---------------------------------------------------------------- */

  /**
   * 曝光观察。
   *
   * ⚠️ 两个必须同时处理的问题：
   *
   * 1) intersectionRatio = 可见面积 ÷ 元素自身面积。元素比视口高时，
   *    无论怎么滚都到不了 50%（2218px 元素 / 844px 视口，理论上限 0.38），
   *    按 >=50% 判定会永远不触发。因此对这类元素改判「填满 90% 视口」。
   *
   * 2) IntersectionObserver 只在「跨过 threshold 刻度」时回调。若把阈值定成
   *    连续值、threshold 却是固定刻度，就会出现死区：
   *    9000px 元素 / 844px 视口 → 需要 0.084，而刻度只有 0.05 和 0.10，
   *    元素最大只能到 0.094，跨不过 0.10 → 一次回调都收不到，曝光成片丢失。
   *    实测 844px 视口下 8442~15190px 是一段连续 6.7k 的死区。
   *
   * 解法：观察前先量元素高度，把算出来的阈值本身放进 threshold 数组，
   * 保证它一定是一个刻度；元素高度变化（懒加载撑高、折叠展开）超过 10% 时按新高度重建。
   */

  /* 兜底刻度，元素高度暂时读不到时用 */
  var OBSERVE_STEPS = (function () {
    var arr = [];
    for (var i = 0; i <= 20; i++) arr.push(i / 20);
    return arr;
  })();

  var RATIO_EPS = 1e-4;   /* 浮点容差：IO 报 0.2999 而阈值 0.3 时不该判为不可见 */

  function effectiveRatio(elHeight, wanted) {
    var vh = w.innerHeight || d.documentElement.clientHeight || 0;
    if (!elHeight || !vh) return wanted;
    var maxPossible = Math.min(1, vh / elHeight);
    if (maxPossible >= wanted) return wanted;
    /* 填满 90% 视口所需的自身比例。
       maxPossible * 0.9 恒小于 maxPossible，所以一定可达；
       下限只用来防 0，取得足够低（0.005 对应元素高 = 180 倍视口）才不会重新制造死区。 */
    return Math.max(0.005, maxPossible * 0.9);
  }

  function observe(el, opts, cb) {
    if (!el || typeof cb !== 'function') return function () {};
    opts = opts || {};
    var ratio = opts.ratio == null ? 0.5 : opts.ratio;
    var dwell = opts.dwell == null ? 1000 : opts.dwell;

    if (!('IntersectionObserver' in w)) return function () {};

    var io = null;
    var timer = null;
    var done = false;
    var boundH = 0;        /* 建 observer 时的元素高度 */
    var boundVH = 0;       /* 建 observer 时的视口高度 */
    var boundNeed = ratio; /* ⚠️ 建 observer 时算出的阈值，判定时必须用它 */
    var builds = 0;        /* 重建次数，防止动画中的元素反复重建 */
    var accumMs = 0;       /* 累计可见时长 */
    var visibleSince = 0;

    function measure() {
      try { return el.getBoundingClientRect().height || 0; } catch (e) { return 0; }
    }
    function vh() { return w.innerHeight || d.documentElement.clientHeight || 0; }

    function build(h) {
      boundH = h;
      boundVH = vh();
      boundNeed = effectiveRatio(h, ratio);
      builds += 1;
      var th = OBSERVE_STEPS.slice();
      /* 关键：把实际阈值塞进刻度表，消除死区 */
      var exists = false;
      for (var i = 0; i < th.length; i++) if (Math.abs(th[i] - boundNeed) < RATIO_EPS) { exists = true; break; }
      if (!exists) { th.push(boundNeed); th.sort(function (a, b) { return a - b; }); }
      if (io) io.disconnect();
      io = new IntersectionObserver(onEntries, { threshold: th });
      io.observe(el);
    }

    /** 需要重建的条件：高度或视口高度变化到让 boundNeed 不再有效 */
    function needsRebuild(h) {
      if (builds > 12) return false;          /* 动画中的元素不无限重建 */
      if (h <= 0) return false;
      if (boundH === 0) return true;
      /* 自愈守卫：当前视口下根本达不到冻结的阈值 —— 必须重建，
         否则冻结阈值反而成了新的死区。移动端地址栏收起/展开时
         resize 事件不一定可靠，所以这条不能只依赖 resize。 */
      if (Math.min(1, vh() / h) < boundNeed - RATIO_EPS) return true;
      if (Math.abs(h - boundH) / boundH > 0.1) return true;
      var cur = vh();
      if (boundVH && Math.abs(cur - boundVH) / boundVH > 0.05) return true;
      return false;
    }

    function cleanup() {
      if (timer) { w.clearTimeout(timer); timer = null; }
      if (io) { io.disconnect(); io = null; }
      d.removeEventListener('visibilitychange', onVis);
      w.removeEventListener('resize', onResize);
    }

    function stop() { done = true; cleanup(); }

    function onEntries(entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (done) return;
        var h = (entry.boundingClientRect && entry.boundingClientRect.height) || 0;

        if (needsRebuild(h)) {
          /* 重建只换刻度，不打断已经在计的 dwell —— 否则高度持续变化的元素
             （懒加载撑高、折叠动画）每次都会把计时清零，曝光永远不触发。 */
          build(h);
          continue;
        }

        /* ⚠️ 判定必须用建 observer 时算出的 boundNeed，不能现算。
           现算的值不在刻度表上，会重新制造死区（这正是上一版的缺陷）。 */
        var visible = entry.isIntersecting && entry.intersectionRatio >= boundNeed - RATIO_EPS;

        /* ⚠️ 零高度元素按 IO 规范 intersectionRatio 恒为 1，会凭空产生曝光，
           而且会把调用方的去重键提前烧掉，让真正的曝光再也发不出来。 */
        if (h <= 0) visible = false;

        /* 后台标签页不算「看到了」 */
        if (visible && d.visibilityState === 'hidden') visible = false;

        if (visible) {
          if (timer) continue;
          visibleSince = Date.now();
          (function (target, observedRatio, requiredRatio) {
            timer = w.setTimeout(function () {
              timer = null;
              done = true;
              accumMs += Date.now() - visibleSince;
              cleanup();
              try {
                cb(target, {
                  ratio_required: Math.round(requiredRatio * 100) / 100,
                  ratio_observed: Math.round(observedRatio * 100) / 100,
                  rule: requiredRatio < ratio - RATIO_EPS ? 'viewport_fill' : 'ratio_' + Math.round(ratio * 100),
                  /* 达标那一刻的累计可见时长。连续观看时约等于 dwell；
                     中途移出再回来则会大于 dwell。 */
                  engaged_ms: accumMs
                });
              } catch (e) { log('observe cb error', e); }
            }, dwell);
          })(entry.target, entry.intersectionRatio, boundNeed);
        } else if (timer) {
          w.clearTimeout(timer);
          timer = null;
          if (visibleSince) { accumMs += Date.now() - visibleSince; visibleSince = 0; }
        }
      }
    }

    /* 切到后台时暂停计时，切回来重新等满 dwell */
    function onVis() {
      if (d.visibilityState === 'hidden' && timer) {
        w.clearTimeout(timer); timer = null;
        if (visibleSince) { accumMs += Date.now() - visibleSince; visibleSince = 0; }
      }
    }
    /* 视口高度变化会让 boundNeed 失效（移动端地址栏收起、旋转屏幕） */
    var resizeTimer = null;
    function onResize() {
      if (done) return;
      if (resizeTimer) w.clearTimeout(resizeTimer);
      resizeTimer = w.setTimeout(function () {
        if (done) return;
        var h = measure();
        if (h > 0) build(h);
      }, 250);
    }

    d.addEventListener('visibilitychange', onVis);
    w.addEventListener('resize', onResize, { passive: true });

    build(measure());

    return stop;
  }

  /* ---------------------------------------------------------------- */
  /* 点击有效性判定                                                    */
  /* ---------------------------------------------------------------- */

  /** 禁用 / 无动作元素 -> false */
  function isInteractive(el) {
    if (!el) return false;
    if (el.disabled === true) return false;
    /* <a class="button" disabled> 这类非表单元素没有 disabled IDL 属性，只能读特性 */
    if (el.getAttribute && el.getAttribute('disabled') != null) return false;
    var ariaDisabled = el.getAttribute && el.getAttribute('aria-disabled');
    if (ariaDisabled === 'true') return false;
    if (el.classList && (el.classList.contains('disabled') || el.classList.contains('pf-disabled'))) return false;
    var st;
    try { st = w.getComputedStyle(el); } catch (e) {}
    if (st && (st.pointerEvents === 'none' || st.display === 'none' || st.visibility === 'hidden')) return false;
    return true;
  }

  /** 该元素是否会产生真实跳转（<a href> 或 PageFly data-href） */
  function hasNavigation(el) {
    if (!el) return false;
    var href = el.getAttribute('href') || el.getAttribute('data-href');
    if (!href) return false;
    if (href === '#' || /^javascript:/i.test(href)) return false;
    /* 同页锚点（#specs / #reviews）不是跳转。PDP 与活动页都有楼层导航，
       算成跳转会把站内锚点点击混进外链/列表点击，destination_url 变成 '#specs'。 */
    if (href.charAt(0) === '#') return false;
    return true;
  }

  /**
   * 点击是否算「成功点击」。
   * 在同一个 tick 之后判定 defaultPrevented，避免比业务脚本先执行。
   */
  function clickWillSucceed(evt, el, requireNavigation) {
    if (!isInteractive(el)) return false;
    if (evt && evt.defaultPrevented && requireNavigation !== false) return false;
    if (requireNavigation && !hasNavigation(el)) return false;
    return true;
  }

  /* ---------------------------------------------------------------- */
  /* 链接类点击：必须「已触发真实跳转」才计成功                          */
  /* ---------------------------------------------------------------- */
  /**
   * 规则原文：点击类仅在用户点击可用元素、且已触发真实跳转/抽屉/状态变更时发送；
   *          阻止跳转不计成功点击。
   *
   * ⚠️ 难点在于站内应用（tmenu 导航）的做法是
   *      stopPropagation() + preventDefault() + 自己 location 跳转 ——
   *    跳转真实发生了，只是不由浏览器默认行为完成，defaultPrevented 却是 true。
   *    这里不做任何推定：默认行为被阻止时把事件**暂存**，
   *    等到页面真的开始卸载（pagehide / beforeunload / visibility→hidden）
   *    或 URL 真的变了（history / popstate）再补发；
   *    超时仍未跳转，说明确实被拦下了，按规则丢弃、不计成功点击。
   */
  var stagedNav = [];
  var navWatchBound = false;

  function flushStagedNav() {
    if (!stagedNav.length) return;
    var q = stagedNav;
    stagedNav = [];
    for (var i = 0; i < q.length; i++) {
      if (q[i].done) continue;
      q[i].done = 1;
      push(q[i].name, q[i].data);
    }
  }

  function bindNavWatch() {
    if (navWatchBound) return;
    navWatchBound = true;
    w.addEventListener('pagehide', flushStagedNav, true);
    w.addEventListener('beforeunload', flushStagedNav, true);
    w.addEventListener('popstate', flushStagedNav, true);
    d.addEventListener('visibilitychange', function () {
      if (d.visibilityState === 'hidden') flushStagedNav();
    }, true);
  }

  /** 暂存到「跳转已确认」再发；NAV_CONFIRM_MS 内没跳走就丢弃 */
  var NAV_CONFIRM_MS = 1500;

  function pushOnNavigation(name, data) {
    bindNavWatch();
    var task = { name: name, data: data, done: 0 };
    var from = w.location.href;
    stagedNav.push(task);
    var waited = 0;
    (function poll() {
      if (task.done) return;
      if (w.location.href !== from) { task.done = 1; push(name, data); return; }
      waited += 100;
      if (waited > NAV_CONFIRM_MS) { task.done = 1; log('跳转未发生，按规则丢弃', name); return; }
      w.setTimeout(poll, 100);
    })();
  }

  /**
   * 链接类点击的统一出口，替代「先 clickWillSucceed 判定、再 DL.push」的旧写法。
   *   · 元素不可用 / 没有真实 href（'#'、javascript:、同页锚点）→ 返回 null，不发
   *   · 默认行为未被阻止 → 浏览器一定会跳，返回的代理立即发送
   *   · 默认行为被阻止   → 返回的代理走 pushOnNavigation，等真实跳转信号
   */
  function navPush(evt, el) {
    if (!isInteractive(el)) return null;
    if (!hasNavigation(el)) return null;
    /* El plugin de menú (smartmenu/tmenu) intercepta el clic de navegación con preventDefault +
       navegación propia por JS. Antes, al estar defaultPrevented, se enviaba vía pushOnNavigation (en
       pagehide) → demasiado tarde: GTM no alcanzaba a procesar el evento ni a enviar el beacon, y el
       tag (nav_click, select_item…) NO disparaba. Ahora enviamos SIEMPRE de inmediato: esto corre en
       el microtask de afterTick (ya tras todo el dispatch, con defaultPrevented en su valor final, pero
       ANTES de que el navegador confirme la navegación, que es una tarea nueva). Es el mismo camino que
       logo_click (enlace normal no interceptado), que sí disparaba. hasNavigation ya garantiza que hay
       un destino real (no '#', no javascript:, no ancla de misma página). */
    return function (name, data) {
      push(name, data);
    };
  }

  /**
   * 延后判定并发送，确保业务脚本已经决定是否 preventDefault。
   *
   * ⚠️ 原实现是 setTimeout(fn, 0)，线上实测被证伪：
   *    点击 tmenu 一级导航（带 href 的普通链接）后，页面在下一个宏任务之前
   *    就完成了导航 —— 探针里 setTimeout 回调**一次都没执行**，
   *    nav_click 100% 丢失。站点开了预取时任何链接类事件都是同样的结局。
   *
   * 改为：处于 click/submit 派发中时把回调排进队列，在 window 冒泡阶段同步清空。
   *    那一刻所有业务 handler 都已执行完（defaultPrevented 是最终值），
   *    但仍在同一次同步派发内，早于导航提交。
   *    若事件在到达 window 之前被 stopPropagation 掐断（这条路径必然没有导航），
   *    再由 setTimeout 兜底；两条路径用 done 标记互斥，不会重复发送。
   */
  /**
   * ⚠️ 原实现是 setTimeout(fn, 0)，线上实测被证伪：
   *    点击 tmenu 一级导航后页面在下一个宏任务之前就完成了导航，
   *    探针里 setTimeout 回调时有时无 —— nav_click 随机丢失。
   *    而「window 冒泡阶段兜底」同样不可靠：tmenu 会 stopPropagation()，
   *    事件根本到不了 window。
   *
   *    微任务是唯一两头都稳的位置：
   *      · 在整次事件派发（含所有冒泡 handler）跑完之后 —— defaultPrevented 已是最终值；
   *      · 又在浏览器提交导航之前（导航是新任务）—— 不会被卸载掐掉；
   *      · 且不受 stopPropagation 影响。
   */
  var MICRO = (typeof w.queueMicrotask === 'function')
    ? function (fn) { w.queueMicrotask(fn); }
    : (typeof Promise === 'function')
      ? function (fn) { Promise.resolve().then(fn); }
      : function (fn) { w.setTimeout(fn, 0); };

  function afterTick(fn) {
    MICRO(function () {
      try { fn(); } catch (e) { log('afterTick 回调异常', e); }
    });
  }

  /* ---------------------------------------------------------------- */
  /* 购物车接口成功侦听（异步成功类事件的统一闸门）                       */
  /* ---------------------------------------------------------------- */

  var cartHandlers = [];
  var CART_ENDPOINTS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  /* 通用接口成功侦听：{ pattern: RegExp, cb: function(url, body) } */
  var requestHandlers = [];

  /**
   * 静态资源不可能代表业务成功。
   * ⚠️ 只对读操作生效：Shopify 的接口本身就以 .js 结尾（/cart/add.js、/cart/update.js），
   * 无差别按扩展名排除会把真实接口一起挡掉。
   */
  var STATIC_ASSET = /\.(js|mjs|css|png|jpe?g|gif|webp|svg|woff2?|ttf|ico|map)(\?|$)/i;

  /** clone 之前先问一句：真的有人关心这个请求吗 */
  function anyRequestHandlerMatches(url, method) {
    var m = String(method || 'GET').toUpperCase();
    for (var i = 0; i < requestHandlers.length; i++) {
      var h = requestHandlers[i];
      try {
        if (!h.pattern.test(url)) continue;
        if ((m === 'GET' || m === 'HEAD') && STATIC_ASSET.test(url)) continue;
        if (h.methods && h.methods.indexOf(m) === -1) continue;
        return true;
      } catch (e) {}
    }
    return false;
  }

  function emitRequestSuccess(url, body, method) {
    var m = String(method || 'GET').toUpperCase();
    requestHandlers.forEach(function (h) {
      try {
        if (!h.pattern.test(url)) return;
        if ((m === 'GET' || m === 'HEAD') && STATIC_ASSET.test(url)) return;
        if (h.methods && h.methods.indexOf(m) === -1) return;
        h.cb(url, body, m);
      } catch (e) { log('request handler error', e); }
    });
  }

  /**
   * 监听任意接口的成功响应（仅 HTTP 2xx）。
   * 用于第三方应用表单（如转盘订阅）的「异步成功类」事件判定。
   */
  function onRequestSuccess(pattern, cb, opts) {
    if (!pattern || typeof cb !== 'function') return;
    opts = opts || {};
    requestHandlers.push({
      pattern: pattern,
      cb: cb,
      /* 默认只认写操作，读接口与静态资源不算业务成功 */
      methods: opts.methods || ['POST', 'PUT', 'PATCH']
    });
  }

  function endpointKind(url) {
    var m = String(url || '').match(/\/cart\/(add|change|update|clear)/i);
    return m ? m[1].toLowerCase() : null;
  }

  function emitCartSuccess(kind, url, body) {
    log('cart api success', kind, url, body);
    cartHandlers.forEach(function (h) {
      try { h(kind, body, url); } catch (e) { log('cart handler error', e); }
    });
  }

  function safeJSON(text) {
    try { return JSON.parse(text); } catch (e) { return null; }
  }

  function installCartInterceptors() {
    /* fetch */
    if (w.fetch) {
      var origFetch = w.fetch;
      w.fetch = function (input, init) {
        var url = '';
        var method = 'GET';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
          method = (init && init.method) || (input && input.method) || 'GET';
        } catch (e) {}
        var p = origFetch.apply(this, arguments);
        /* 必须先判有没有 handler 关心这个 URL，再决定要不要 clone。
           否则页面上每一个 2xx 响应都会被整段缓冲 + JSON.parse——
           Section Rendering API 返回的大块 HTML 也不例外，实打实拖慢业务。 */
        if (url && anyRequestHandlerMatches(url, method)) {
          p.then(function (res) {
            if (!res || !res.ok) return res;
            try {
              res.clone().text().then(function (txt) {
                emitRequestSuccess(url, safeJSON(txt) || txt, method);
              }).catch(function () { emitRequestSuccess(url, null, method); });
            } catch (e) { emitRequestSuccess(url, null, method); }
            return res;
          }).catch(function () {});
        }
        if (url && CART_ENDPOINTS.test(url)) {
          p.then(function (res) {
            if (!res || !res.ok) return res;
            try {
              res.clone().json().then(function (body) {
                emitCartSuccess(endpointKind(url), url, body);
              }).catch(function () {
                emitCartSuccess(endpointKind(url), url, null);
              });
            } catch (e) {
              emitCartSuccess(endpointKind(url), url, null);
            }
            return res;
          }).catch(function () { /* 接口失败不发事件 */ });
        }
        return p;
      };
    }

    /* XMLHttpRequest */
    if (w.XMLHttpRequest && w.XMLHttpRequest.prototype) {
      var proto = w.XMLHttpRequest.prototype;
      var origOpen = proto.open;
      var origSend = proto.send;
      proto.open = function (method, url) {
        try { this.__djiUrl = url; this.__djiMethod = method; } catch (e) {}
        return origOpen.apply(this, arguments);
      };
      proto.send = function () {
        var xhr = this;
        /* 监听器每个 XHR 实例只绑一次。原来每次 send 都新增一个且从不移除，
           复用同一实例 open/send 两次的库会让 add_to_cart 翻倍，
           而且旧监听闭包里捕获的是上一次的 url。 */
        if (!xhr.__djiBound) {
          xhr.__djiBound = 1;
          xhr.addEventListener('load', function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            var u = xhr.__djiUrl || '';          /* 读当次 open 写入的值 */
            if (!u) return;
            var body = safeJSON(xhr.responseText);
            if (CART_ENDPOINTS.test(u)) emitCartSuccess(endpointKind(u), u, body);
            if (anyRequestHandlerMatches(u, xhr.__djiMethod)) {
              emitRequestSuccess(u, body || xhr.responseText, xhr.__djiMethod);
            }
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  function onCartSuccess(cb) {
    if (typeof cb === 'function') cartHandlers.push(cb);
  }

  function fetchCart() {
    return fetch(w.Shopify && w.Shopify.routes && w.Shopify.routes.root ? w.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  /* ---------------------------------------------------------------- */
  /* items[] 构造                                                      */
  /* ---------------------------------------------------------------- */

  var TOP_CATEGORIES = CTX.top_categories || [];

  function money(cents) {
    /* isNaN('') 是 false，isNaN(false) 也是 false —— 不拦住会得到 price: 0，
       并被 itemsValue 累加，让 value 系统性偏低。 */
    if (cents == null || cents === '' || typeof cents === 'boolean') return undefined;
    if (isNaN(cents)) return undefined;
    return Math.round(Number(cents)) / 100;
  }

  function productCollections(productId) {
    var map = w.__pagefly_product_collections || {};
    return map[String(productId)] || [];
  }

  /**
   * 按 item_category / item_category2 / item_series 规则拆解商品所属集合。
   * @param {string[]} collectionTitles 商品所属集合标题
   * @param {string}   productType      Shopify product.type
   */
  /**
   * 顶级品类匹配用归一化后的字符串比对。
   * ⚠️ 原来是 TOP_CATEGORIES.indexOf(title) 精确匹配，大小写或重音差一个字符就落空，
   * item_category 会**静默消失**（实测：top_categories 里是「Dispositivos portátiles」，
   * 而商品 collections 里是「Dispositivos Portátiles」，大写 P，于是整组商品都没有 item_category）。
   * top_categories 来自 Liquid，首页的商品 collections 来自 PageFly 的数据，
   * 两边不保证大小写一致，必须归一化后再比。
   * 输出仍用原始字符串，不改变上报值。
   */
  function normCat(s) {
    return String(s || '').toLowerCase()
      .replace(/[áàäâ]/g, 'a').replace(/[éèëê]/g, 'e').replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o').replace(/[úùüû]/g, 'u').replace(/ñ/g, 'n')
      .replace(/\s+/g, ' ').trim();
  }

  function splitCategories(collectionTitles, productType) {
    var cols = (collectionTitles || []).filter(Boolean);
    var tops = {};
    for (var t = 0; t < TOP_CATEGORIES.length; t++) tops[normCat(TOP_CATEGORIES[t])] = 1;
    var top = '';
    var series = '';
    for (var i = 0; i < cols.length; i++) {
      var isTop = tops[normCat(cols[i])] === 1;
      if (!top && isTop) { top = cols[i]; continue; }
      if (!series && !isTop && !/^Product Display/i.test(cols[i])) series = cols[i];
    }
    return {
      item_category: top || undefined,
      item_category2: series || undefined,
      item_category3: productType || undefined,
      item_series: series || undefined
    };
  }

  function categorize(product, productId) {
    return splitCategories(
      productCollections(productId).map(function (c) { return c && c.title; }),
      product && product.type
    );
  }

  /**
   * 构造单个 item。
   * @param {string|number} productId  Shopify product id
   * @param {object} extra  { variant_id, quantity, index, item_list_id, item_list_name, price, item_variant, ... }
   */
  function item(productId, extra) {
    extra = extra || {};
    var pid = String(productId || extra.item_id || '');
    var p = (w.__pageflyProducts || {})[pid] || null;

    var variant = null;
    if (p && p.variants && p.variants.length) {
      if (extra.variant_id) {
        for (var i = 0; i < p.variants.length; i++) {
          if (String(p.variants[i].id) === String(extra.variant_id)) { variant = p.variants[i]; break; }
        }
      }
      if (!variant) variant = p.selected_or_first_available_variant || p.variants[0];
    }

    var cat = categorize(p, pid);

    var out = {
      item_id: pid || undefined,
      item_name: extra.item_name || (p && p.title) || undefined,
      item_brand: CTX.brand || 'DJI',
      item_category: extra.item_category || cat.item_category,
      item_category2: extra.item_category2 || cat.item_category2,
      item_category3: extra.item_category3 || cat.item_category3,
      item_series: extra.item_series || cat.item_series,
      item_model: extra.item_model || (p && p.title) || undefined,
      item_variant: extra.item_variant || (variant && variant.title) || undefined,
      selected_variant_id: extra.variant_id ? String(extra.variant_id) : (variant && String(variant.id)) || undefined,
      product_handle: extra.product_handle || (p && p.handle) || undefined,
      availability: extra.availability || (variant ? (variant.available ? 'in_stock' : 'out_of_stock') : undefined),
      price: extra.price != null ? extra.price : money(variant && variant.price),
      quantity: extra.quantity == null ? 1 : extra.quantity,
      item_list_id: extra.item_list_id,
      item_list_name: extra.item_list_name,
      index: extra.index
    };
    return clean(out);
  }

  /** 由 /cart.js 或 /cart/add.js 返回的行构造 items[] */
  function itemsFromCartLines(lines, extra) {
    extra = extra || {};
    return (lines || []).map(function (line, i) {
      return item(line.product_id, {
        item_name: line.product_title || line.title,
        variant_id: line.variant_id,
        item_variant: line.variant_title || undefined,
        product_handle: line.handle,
        price: money(line.final_price != null ? line.final_price : line.price),
        quantity: line.quantity,
        index: extra.index != null ? extra.index : i,
        item_list_id: extra.item_list_id,
        item_list_name: extra.item_list_name,
        item_category: extra.item_category,
        item_series: extra.item_series
      });
    });
  }

  function itemsValue(items) {
    var v = 0;
    (items || []).forEach(function (it) {
      if (it.price != null) v += Number(it.price) * (it.quantity || 1);
    });
    return Math.round(v * 100) / 100;
  }

  /* ---------------------------------------------------------------- */
  /* 通用整页滚动深度                                                    */
  /* ---------------------------------------------------------------- */

  /**
   * 表格口径（首页 H8 / PDP P4）：
   *   - 首次达到 30 / 50 / 70 / 100% 时各触发一次
   *   - 以 page_view_id + scroll_percent 去重，刷新页面重新计算
   *   - 100% 判定：scrollY + viewportHeight >= documentHeight - 2px
   *   - 初始加载、未跨阈值滚动、组件重渲染均不触发
   *
   * @param {object}   opts { thresholds, eventName }
   * @param {function} cb   cb(scroll_percent) -> 由调用方补充来源字段后 push
   */
  function scrollDepth(opts, cb) {
    opts = opts || {};
    var thresholds = (opts.thresholds || [30, 50, 70, 100]).slice().sort(function (a, b) { return a - b; });
    var key = opts.key || 'scroll';
    var maxPct = 0;
    var ticking = false;

    /* 秒切换商品后新页面从头算：不重置的话，上一个商品滚到 70% 会把
       新页面的 30/50 两档整个压掉，数据丢不丢完全取决于用户上次滚了多远。 */
    onNewPageView(function () { maxPct = 0; });

    function docHeight() {
      return Math.max(
        d.documentElement.scrollHeight,
        d.body ? d.body.scrollHeight : 0
      );
    }

    function currentPct() {
      var total = docHeight();
      if (!total) return 0;
      var seen = (w.scrollY || w.pageYOffset || 0) + w.innerHeight;
      /* 100% 判定留 2px 容差，与表格一致 */
      if (seen >= total - 2) return 100;
      return Math.min(100, Math.floor((seen / total) * 100));
    }

    function check() {
      ticking = false;
      var pct = currentPct();
      if (pct <= maxPct) return;
      maxPct = pct;
      for (var i = 0; i < thresholds.length; i++) {
        var t = thresholds[i];
        if (pct < t) continue;
        /* page_view 级去重：刷新后 PAGE_VIEW_ID 变化，自动重新计算 */
        if (!once(key + '|' + t)) continue;
        try { cb(t, docHeight()); } catch (e) { log('scrollDepth cb error', e); }
      }
    }

    w.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      w.requestAnimationFrame(check);
    }, { passive: true });

    /* 首检只处理「页面根本不可滚动」这一种情况——此时首屏就是全部内容，
       100% 是真的读完了。可滚动的页面在用户实际滚动之前不产生任何档位：
       currentPct() 算的是 (scrollY + innerHeight) / docHeight，两屏高的页面
       在 scrollY=0 时就已经是 50%，无条件首检会凭空发出 30 和 50 两档，
       与本函数「初始加载不触发」的约定直接冲突，并系统性虚高滚动率。

       ⚠️ 判定不能只做一次：慢网络下 1200ms 时懒加载图片还没撑开页面，
       此刻看起来「不可滚动」→ 会一次性发出 30/50/70/100 四档，
       之后内容加载完页面变长，maxPct 已锁死 100，真实阅读深度再也发不出来。
       所以要等页面高度稳定下来（连续两次测量一致）才认这个判断。 */
    var stableH = -1;
    var settleTries = 0;
    function initialCheck() {
      var total = docHeight();
      var vh = w.innerHeight || 0;
      if (!total || !vh) return;

      if (total !== stableH) {
        /* 高度还在变，继续等 */
        stableH = total;
        if (++settleTries < 8) { w.setTimeout(initialCheck, 1200); }
        return;
      }
      /* 连续两次高度一致 = 已稳定。此时仍不可滚动才算「首屏即全部」。 */
      if (total <= vh + 2) check();
    }
    w.setTimeout(initialCheck, 1200);
  }

  /* ---------------------------------------------------------------- */

  installCartInterceptors();

  w.DJIDataLayer = {
    push: push,
    setPageVars: setPageVars,
    common: commonFields,
    uuid: uuid,
    pageViewId: PAGE_VIEW_ID,
    once: once,
    onceSession: onceSession,
    newPageView: newPageView,
    onNewPageView: onNewPageView,
    observe: observe,
    scrollDepth: scrollDepth,
    deviceType: deviceType,
    onCartSuccess: onCartSuccess,
    onRequestSuccess: onRequestSuccess,
    fetchCart: fetchCart,
    item: item,
    splitCategories: splitCategories,
    itemsFromCartLines: itemsFromCartLines,
    itemsValue: itemsValue,
    money: money,
    sanitize: sanitize,
    isInteractive: isInteractive,
    hasNavigation: hasNavigation,
    clickWillSucceed: clickWillSucceed,
    afterTick: afterTick,
    clean: clean,
    log: log,
    ctx: CTX,
    navPush: navPush,
    pushOnNavigation: pushOnNavigation,
    debug: DEBUG
  };

  log('core ready', CTX);
})(window, document);
